import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Receipt, 
  Download, 
  TrendingUp, 
  Building2, 
  FileText,
  Euro,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { ExportColumnsDialog } from "../components/ExportColumnsDialog";
import DataStateError from "../components/DataStateError";
// 29/09/2026 — colonna "Scadenza" (e conguagli) in gg/mm/aaaa: prima erano ISO.
import { formatDate } from "../lib/format";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

export default function CommissionReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' niente" da "il caricamento e' fallito": senza questo
  // un guasto di rete sembrava una pagina vuota.
  const [loadError, setLoadError] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // 21/10/2026: l'export puo' coprire un periodo (dal mese al mese) e una compagnia
  // diversi da quelli a video, es. "Verti da gennaio a oggi".
  const [exportCompany, setExportCompany] = useState("all");
  const [exportFrom, setExportFrom] = useState({ month: 1, year: new Date().getFullYear() });
  const [exportTo, setExportTo] = useState({ month: 1, year: new Date().getFullYear() });

  const openExportDialog = () => {
    setExportCompany(selectedCompany);
    setExportFrom({ month: selectedMonth, year: selectedYear });
    setExportTo({ month: selectedMonth, year: selectedYear });
    setExportDialogOpen(true);
  };

  const periodoValido = exportTo.year * 12 + exportTo.month >= exportFrom.year * 12 + exportFrom.month;
  const annoCorrente = new Date().getFullYear();
  const ANNI = Array.from({ length: annoCorrente - 2019 + 1 }, (_, i) => 2020 + i);

  useEffect(() => {
    fetchReport();
  }, [selectedMonth, selectedYear, selectedCompany]);

  // v3.8: conteggio rate semestrali del mese (il backend le etichetta installment_type)
  const semiStats = () => {
    let n = 0, cons = 0;
    (report?.by_company || []).forEach((c) => {
      (c.policies || []).forEach((p) => {
        if (p.installment_type === "Rata semestrale") {
          n += 1;
          cons += p.consultation_fee || 0;
        }
      });
    });
    return { n, cons };
  };

  const fetchReport = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      let url = `/api/reports/commissions?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedCompany && selectedCompany !== "all") {
        url += `&company=${encodeURIComponent(selectedCompany)}`;
      }
      const response = await axios.get(url);
      setReport(response.data);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format, columns, sortBy, sortDir, landscape, includeArrears) => {
    if (!periodoValido) {
      toast.error("Il mese finale viene prima di quello iniziale");
      return;
    }
    setExporting(true);
    try {
      let url = `/api/reports/commissions/export?format=${format}&month=${exportFrom.month}&year=${exportFrom.year}`;
      url += `&month_to=${exportTo.month}&year_to=${exportTo.year}`;
      if (columns) url += `&columns=${columns.join(",")}`;
      if (sortBy && sortBy !== "none") {
        url += `&sort_by=${sortBy}&sort_dir=${sortDir || "asc"}`;
      }
      if (landscape) url += `&landscape=true`;
      if (includeArrears) url += `&include_arrears=true`;
      if (exportCompany && exportCompany !== "all") url += `&company=${encodeURIComponent(exportCompany)}`;

      const response = await axios.get(url, { responseType: 'blob' });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const unMese = exportFrom.month === exportTo.month && exportFrom.year === exportTo.year;
      const periodo = unMese
        ? `${exportFrom.month}_${exportFrom.year}`
        : `${exportFrom.month}_${exportFrom.year}-${exportTo.month}_${exportTo.year}`;
      const compagnia = exportCompany && exportCompany !== "all" ? `_${exportCompany.replace(/[^A-Za-z0-9]+/g, "-")}` : "";
      link.setAttribute('download', `commissioni_${periodo}${compagnia}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(`Report ${format.toUpperCase()} scaricato`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading && !report) {
    return (
      <div data-testid="commission-report-loading" className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Skeleton className="h-9 w-56 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>

        {/* Month Navigation Skeleton */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-9 w-9" />
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-9 w-9" />
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards Skeleton */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="pagina-errore-caricamento">
        <DataStateError
          titolo="Non riesco a caricare il report"
          messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto."
          onRetry={fetchReport}
        />
      </div>
    );
  }

  return (
    <div data-testid="commission-report-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight flex items-center gap-3">
            <Receipt className="h-7 w-7 text-primary" />
            Report Commissioni
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Analisi mensile di premi e consulenze
          </p>
        </div>
        
        {/* Export button */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={openExportDialog}
            disabled={exporting}
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Esporta
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <Card className="bg-card border-border">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => changeMonth(-1)}
                data-testid="prev-month-btn"
               aria-label="Mese precedente">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-2 min-w-[180px] justify-center">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold">
                  {MONTHS[selectedMonth - 1]} {selectedYear}
                </span>
              </div>
              
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => changeMonth(1)}
                data-testid="next-month-btn"
               aria-label="Mese successivo">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Company Filter */}
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Select
                value={selectedCompany}
                onValueChange={setSelectedCompany}
              >
                <SelectTrigger className="w-[180px] h-9" data-testid="company-filter">
                  <SelectValue placeholder="Tutte le compagnie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le compagnie</SelectItem>
                  {report?.available_companies?.map((company) => (
                    <SelectItem key={company} value={company}>
                      {company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards - 2x2 grid su mobile, 4 colonne su desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Polizze</p>
                <p className="text-xl font-bold mt-1" data-testid="total-policies">
                  {report?.summary?.total_policies || 0}
                </p>
              </div>
              <FileText className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Premi</p>
                <p className="text-xl font-bold text-green-600 mt-1" data-testid="total-premium">
                  {formatCurrency(report?.summary?.total_premium)}
                </p>
              </div>
              <Euro className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Consulenze</p>
                <p className="text-xl font-bold text-blue-600 mt-1" data-testid="total-consultation">
                  {formatCurrency(report?.summary?.total_consultation)}
                </p>
                {semiStats().n > 0 && (
                  <p className="text-[11px] text-orange-600 mt-1">
                    di cui {semiStats().n} rate semestrali: {formatCurrency(semiStats().cons)}
                  </p>
                )}
              </div>
              <Receipt className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Totale</p>
                <p className="text-xl font-bold text-primary mt-1" data-testid="total-revenue">
                  {formatCurrency(report?.summary?.total_revenue)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Company Breakdown */}
      {report?.by_company?.length > 0 && (
        <div className="space-y-4">
          {report.by_company.map((company) => (
            <Card 
              key={company.company} 
              className="bg-card border-border overflow-hidden"
              data-testid={`company-${company.company}`}
            >
              {/* Company Header */}
              <div className="bg-muted/50 px-4 py-3 border-b border-border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold">{company.company}</h3>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                      {company.count} polizze
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">
                      Premi: {formatCurrency(company.total_premium)}
                    </span>
                    <span className="text-blue-600 font-medium">
                      Cons.: {formatCurrency(company.total_consultation)}
                    </span>
                    <span className="text-primary font-bold">
                      Tot: {formatCurrency(company.total_premium + company.total_consultation)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Policies table - layout migliorato */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-[200px] font-semibold">Cliente</TableHead>
                      <TableHead className="hidden md:table-cell w-[100px] font-semibold">Tipo</TableHead>
                      <TableHead className="hidden md:table-cell w-[100px] font-semibold">Scadenza</TableHead>
                      <TableHead className="w-[100px] text-right font-semibold">Premio</TableHead>
                      <TableHead className="hidden md:table-cell w-[100px] text-right font-semibold">Cons.</TableHead>
                      <TableHead className="w-[100px] text-right font-semibold">Totale</TableHead>
                      <TableHead className="w-[80px] text-center font-semibold">Rinn.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {company.policies.map((policy, idx) => (
                      <TableRow 
                        key={policy.policy_id}
                        className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}
                      >
                        <TableCell className="font-medium">
                          <div className="truncate max-w-[200px]" title={policy.client_name}>
                            {policy.client_name}
                          </div>
                          {policy.license_plate && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {policy.license_plate}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className="text-xs capitalize bg-muted px-1.5 py-0.5 rounded">
                            {policy.policy_type}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {policy.installment_type === "Rata semestrale" ? (
                            <div>
                              <div className="font-semibold text-orange-700">
                                {formatDate(policy.semiannual_expiry, "-")}
                              </div>
                              <span className="inline-block text-[10px] font-medium bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded mt-0.5">
                                Rata semestrale
                              </span>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                Annuale: {formatDate(policy.expiry_date, "-")}
                              </div>
                            </div>
                          ) : (
                            <div>
                              {formatDate(policy.expiry_date, "-")}
                              {policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
                                <div className="text-xs text-orange-600">
                                  Sem: {formatDate(policy.semiannual_expiry, "-")}
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          {formatCurrency(policy.premium)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-right font-mono text-blue-600">
                          {formatCurrency(policy.consultation_fee)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(policy.premium + policy.consultation_fee)}
                        </TableCell>
                        <TableCell className="text-center">
                          {policy.renewed ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* SEZIONE CONGUAGLI (cambio mezzo, variazioni) */}
      {report?.adjustments?.length > 0 && (
        <Card className="bg-card border-blue-300/50 overflow-hidden">
          <div className="p-4 border-b border-border bg-blue-50/50">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-semibold text-blue-800">Conguagli e variazioni</h3>
                <p className="text-xs text-muted-foreground">
                  {report.adjustments.length} variazioni incassate nel mese (es. cambio mezzo)
                </p>
              </div>
              <div className="text-right text-sm">
                <div>Premi: {formatCurrency(report.summary.adjustments_premium)}</div>
                <div>Cons.: {formatCurrency(report.summary.adjustments_consultation)}</div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="font-semibold">Compagnia</TableHead>
                  <TableHead className="hidden md:table-cell font-semibold">N. Polizza</TableHead>
                  <TableHead className="font-semibold">Variazione</TableHead>
                  <TableHead className="font-semibold">Data</TableHead>
                  <TableHead className="text-right font-semibold">Premio</TableHead>
                  <TableHead className="text-right font-semibold">Cons.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.adjustments.map((adj, idx) => (
                  <TableRow key={idx} className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                    <TableCell className="font-medium">
                      <div className="truncate max-w-[200px]" title={adj.client_name}>
                        {adj.client_name}
                      </div>
                      {adj.license_plate && (
                        <span className="text-xs text-muted-foreground font-mono">{adj.license_plate}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {adj.company || "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs">
                      {adj.policy_number || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {adj.description || adj.type}
                      {adj.appendix_number && (
                        <div className="text-xs text-muted-foreground">App. {adj.appendix_number}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(adj.date, "-")}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {formatCurrency(adj.premium_adjustment)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-blue-600">
                      {formatCurrency(adj.consultation_adjustment)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* SEZIONE ARRETRATI */}
      {report?.arrears?.length > 0 && (
        <Card className="bg-card border-amber-300/50 overflow-hidden" data-testid="arrears-section">
          <div className="bg-amber-50 dark:bg-amber-950/20 px-4 py-3 border-b border-amber-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-amber-600" />
                <h3 className="font-semibold text-amber-800 dark:text-amber-200">Arretrati</h3>
                <span className="text-xs text-amber-700 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded">
                  {report.arrears.length} polizze registrate in ritardo
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600 font-medium">
                  Premi: {formatCurrency(report.summary.arrears_premium)}
                </span>
                <span className="text-blue-600 font-medium">
                  Cons.: {formatCurrency(report.summary.arrears_consultation)}
                </span>
                <span className="text-amber-700 dark:text-amber-300 font-bold">
                  Tot: {formatCurrency(report.summary.arrears_premium + report.summary.arrears_consultation)}
                </span>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50/50 dark:bg-amber-950/10">
                  <TableHead className="w-[200px] font-semibold">Cliente</TableHead>
                  <TableHead className="w-[100px] font-semibold">Compagnia</TableHead>
                  <TableHead className="hidden md:table-cell w-[100px] font-semibold">Tipo</TableHead>
                  <TableHead className="w-[100px] font-semibold">Mese orig.</TableHead>
                  <TableHead className="w-[100px] text-right font-semibold">Premio</TableHead>
                  <TableHead className="hidden md:table-cell w-[100px] text-right font-semibold">Cons.</TableHead>
                  <TableHead className="w-[100px] text-right font-semibold">Totale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.arrears.map((arrear, idx) => (
                  <TableRow 
                    key={arrear.policy_id}
                    className={idx % 2 === 0 ? "bg-background" : "bg-amber-50/30 dark:bg-amber-950/5"}
                  >
                    <TableCell className="font-medium">
                      <div className="truncate max-w-[200px]" title={arrear.client_name}>
                        {arrear.client_name}
                      </div>
                      {arrear.license_plate && (
                        <span className="text-xs text-muted-foreground font-mono">
                          {arrear.license_plate}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{arrear.company}</TableCell>
                    <TableCell>
                      <span className="text-xs capitalize bg-muted px-1.5 py-0.5 rounded">
                        {arrear.policy_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded font-medium">
                        {arrear.original_month}
                      </span>
                      {arrear.arrear_type === "semestrale" && (
                        <span className="text-xs text-orange-600 ml-1">(Sem.)</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {formatCurrency(arrear.premium)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-blue-600">
                      {formatCurrency(arrear.consultation_fee)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(arrear.premium + arrear.consultation_fee)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {report?.by_company?.length === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="py-12">
            <div className="text-center">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nessuna polizza</h3>
              <p className="text-muted-foreground">
                Non ci sono polizze in scadenza in {MONTHS[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      <ExportColumnsDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        exportType="commissions"
        onExport={handleExport}
        exporting={exporting}
      >
        <div className="space-y-3 p-3 rounded-md border bg-muted/30" data-testid="export-periodo">
          <div className="space-y-1.5">
            <label className="text-sm font-medium flex items-center gap-1.5">
              <Building2 className="h-4 w-4" /> Compagnia
            </label>
            <Select value={exportCompany} onValueChange={setExportCompany}>
              <SelectTrigger className="h-9" data-testid="export-company">
                <SelectValue placeholder="Tutte le compagnie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le compagnie</SelectItem>
                {report?.available_companies?.map((company) => (
                  <SelectItem key={company} value={company}>{company}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> Periodo
            </label>
            {[["Dal", exportFrom, setExportFrom, "export-dal"], ["Al", exportTo, setExportTo, "export-al"]].map(
              ([etichetta, valore, imposta, testid]) => (
                <div key={etichetta} className="flex items-center gap-2">
                  <span className="w-8 text-sm text-muted-foreground">{etichetta}</span>
                  <Select
                    value={String(valore.month)}
                    onValueChange={(m) => imposta({ ...valore, month: Number(m) })}
                  >
                    <SelectTrigger className="h-9 flex-1" data-testid={`${testid}-mese`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((nome, i) => (
                        <SelectItem key={nome} value={String(i + 1)}>{nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(valore.year)}
                    onValueChange={(y) => imposta({ ...valore, year: Number(y) })}
                  >
                    <SelectTrigger className="h-9 w-24" data-testid={`${testid}-anno`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ANNI.map((a) => (
                        <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )
            )}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Button
                type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => {
                  setExportFrom({ month: selectedMonth, year: selectedYear });
                  setExportTo({ month: selectedMonth, year: selectedYear });
                }}
              >
                Solo {MONTHS[selectedMonth - 1]}
              </Button>
              <Button
                type="button" variant="outline" size="sm" className="h-7 text-xs"
                data-testid="export-da-inizio-anno"
                onClick={() => {
                  const oggi = new Date();
                  setExportFrom({ month: 1, year: oggi.getFullYear() });
                  setExportTo({ month: oggi.getMonth() + 1, year: oggi.getFullYear() });
                }}
              >
                Da gennaio a oggi
              </Button>
              <Button
                type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => {
                  setExportFrom({ month: 1, year: selectedYear });
                  setExportTo({ month: 12, year: selectedYear });
                }}
              >
                Tutto il {selectedYear}
              </Button>
            </div>
            {!periodoValido && (
              <p className="text-xs text-destructive">Il mese finale viene prima di quello iniziale.</p>
            )}
            <p className="text-xs text-muted-foreground">
              Per un report con i soli premi (senza compensi), togli le colonne «Consulenza» e «Totale».
            </p>
          </div>
        </div>
      </ExportColumnsDialog>
    </div>
  );
}
